import React from 'react';
import { DataTitle, DataValue, NoData } from 'src/common';
import FormulaPreview from 'src/components/FormulaPreview';
import { isPlainNumberFormula } from 'src/components/FormulaInput/shared/formula';
import { formatNumber } from 'src/utils/misc';
import { CostDisplayMode, resolveCostDisplay } from './util';
import { CalculatedIcon } from './CostCellContent';
import { MissingVariableBanner } from './MissingVariableBanner';
import * as S from './CostCellContent.styles';

interface CostDetailBlockProps {
  title: string;
  /** Evaluated amount (rmp mode). Library mode never shows amounts. */
  value?: number | null;
  formula?: string | null;
  mode: CostDisplayMode;
  /** Opens the edit modal focused on this cost (absent in read-only contexts). */
  onEdit?: () => void;
}

/**
 * Details-sheet block for one cost field: amount (or Missing-variables banner) in
 * RMP mode, plus the formula rendered as variable pills; library mode shows the
 * formula only (a plain-number formula renders as a bare number).
 */
export const CostDetailBlock: React.FC<CostDetailBlockProps> = ({ title, value, formula, mode, onEdit }) => {
  const display = resolveCostDisplay({ value, formula, mode });
  const hasVariableFormula = formula != null && formula.trim() !== '' && !isPlainNumberFormula(formula);

  return (
    <div data-cy="cost-detail-block">
      <DataTitle>{title}</DataTitle>
      {mode === 'rmp' && display.state === 'value' && (
        <S.Content>
          <DataValue>{formatNumber(display.value, { symbol: true, padDecimals: true })}</DataValue>
          {display.calculated && <CalculatedIcon />}
        </S.Content>
      )}
      {mode === 'rmp' && display.state === 'missing' && <MissingVariableBanner onEdit={onEdit} />}
      {display.state === 'blank' && <NoData />}
      {mode === 'library' && display.state === 'value' && (
        <DataValue>{formatNumber(display.value, { symbol: true, padDecimals: true })}</DataValue>
      )}
      {hasVariableFormula && (
        <S.FormulaRow>
          <FormulaPreview value={formula} showValueOnHover />
        </S.FormulaRow>
      )}
    </div>
  );
};
